import { Component, OnInit } from '@angular/core';
import { WorkbenchConfiguratorItem } from '../../configurator/applications/workbench-configurator-item';
import { WorkbenchService } from '../../../../services/configurators-2d/applications/workbench.service';
import { Cutaway } from 'src/app/models/configurators-2d/applications/workbench/accessories/cutaway';
import {
  ConfigurationPlan,
  isPlanColorValid,
} from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { Configurators2DComponent } from '../../configurators-2d.component';
import { IWorkbenchPreview } from '../preview/i-workbench-preview';
import { ConfigurationGroup } from '../../../../models/configurators-2d/applications/workbench/configurations/configuration-group';
import { ShapeTypeEnum } from '../../../../enum/workbench/shape-type.enum';
import { MaterialTypeEnum } from 'src/app/enum/workbench/material-type-enum';
import { Cutting } from 'src/app/models/configurators-2d/applications/workbench/cutting';
import { allSplashbackHaveHeight } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration';

@Component({
  selector: 'ng-workbench-finishes',
  templateUrl: './finishes.component.html',
})
export class WorkbenchFinishesComponent extends WorkbenchConfiguratorItem implements OnInit {
  preview: IWorkbenchPreview;

  // Liste des découpes d'angles
  cutaways: Cutaway[];
  // Ouvertures de plans
  cuttings: Cutting[];

  substep: number;

  planOrGroupSelected: ConfigurationPlan | ConfigurationGroup;

  constructor(
    public workbenchService: WorkbenchService,
    configurators2DComponent: Configurators2DComponent
  ) {
    super(workbenchService);
    this.preview = configurators2DComponent.preview as IWorkbenchPreview;
  }

  ngOnInit(): void {
    // Légende
    this.preview.updateLegend(false);

    // Découpe d'angle
    this.service.getCutaways().then(cutaways => {
      this.cutaways = Array.from(
        cutaways
          .reduce((map, cutaway) => {
            map.set(cutaway.name, cutaway);
            return map;
          }, new Map())
          .values()
      );
    });

    // Ouvertures de plans
    if (this.configurationCanHaveCutting()) {
      this.service.getCuttings(this.configuration.workbenchConfigurationPlans[0].material.id).then(cuttings => {
        this.cuttings = cuttings.filter(r => r.isWorkbenchCutting);

        // Si des plans existent avec une couleur on affecte la couleur
        if (this.configuration.workbenchConfigurationPlans.length > 0) {
          this.configuration.workbenchConfigurationPlans.forEach(plan => {
            if (plan.color != null) {
              this.preview.loadImage(plan.color.picture.path, plan.displayOrder);
            }
          });
        }

        // Si des plans ont des ouvertures, on charge les ouvertures et on les load
        if (this.configuration.workbenchConfigurationPlans.some(p => p?.configurationCuttings?.length > 0)) {
          for (let plan of this.configuration.workbenchConfigurationPlans) {
            if (plan.configurationCuttings != null) {
              for (let cutting of plan.configurationCuttings) {
                cutting.cutting = this.cuttings?.find(c => c.id == cutting.cuttingId);
              }
            }
          }

          this.preview.loadPlanCuttings();
        }
      });
    }

    // désélection des formes
    this.preview.previewSelectPlanById(null);

    this.preview.actionByConfiguratorStep();

    this.preview.recreateEdgesCutawaysAndJambs();
  }

  toggleCollapse(substep: number): void {
    if (this.substep !== substep) {
      this.substep = substep;
      this.preview.showEdgeCutawayAndJambsSelected();

      switch (this.substep) {
        case 1:
          this.preview.showEdgeControls(true);
          break;

        case 2:
          this.preview.showEdgeControls(true);
          break;

        case 3:
          this.preview.showJambControls(true);
          break;

        case 4:
          this.preview.showCutawayControls(true);
          break;

        case 5:
          this.preview.showCuttingControls?.(true);
          break;
      }
    }
  }

  isNextButtonActive(): boolean {
    const hasJambs = this.configuration.workbenchConfigurationPlans.some(p => p.configurationJambs?.length > 0);
    const allJambsAreComplete = this.configuration.workbenchConfigurationPlans.every(p =>
      !p.configurationJambs?.length || p.configurationJambs.every(j => j.height != null && j.height > 0)
    );

    return !hasJambs || (hasJambs && allJambsAreComplete);
  }

  checkPageChangeCapability(): boolean {
    // Si la configuration comporte du quartz on vérifie les crédences
    if (
      this.configuration.workbenchConfigurationPlans.some(
        plan => plan.material?.materialType === MaterialTypeEnum.Quartz
      )
    ) {
      return (
        !this.configuration.workbenchConfigurationPlans.some(plan => plan?.configurationSplashbacks?.length > 0) ||
        (this.configuration.workbenchConfigurationPlans.some(plan => plan?.configurationSplashbacks?.length > 0) &&
          allSplashbackHaveHeight(this.configuration))
      );
    } else {
      // Sinon on fait le traitement classique.
      return this.configuration.workbenchConfigurationPlans.every(plan => isPlanColorValid(plan) && plan.material);
    }
  }

  configurationCanHaveEdges(): boolean {
    return this.configuration.workbenchConfigurationPlans.some(p => p.materialThickness.canHaveEdges);
  }

  configurationCanHaveSideCuts(): boolean {
    return this.configuration.workbenchConfigurationPlans.some(
      p => p?.materialThickness?.canHaveSideCuts /*&& p?.color?.range?.name === SILESTONE_NAME*/
    );
  }

  configurationCanHaveJambs(): boolean {
    if (
      this.configuration.workbenchConfigurationPlans.every(p => p.material?.materialType === MaterialTypeEnum.Quartz)
    ) {
      return this.configuration.workbenchConfigurationPlans.every(
        p =>
          p?.materialThickness?.canHaveJambs &&
          p.shape.shapeType !== ShapeTypeEnum.Angle /*&& p?.color?.range?.name === SILESTONE_NAME*/
      );
    }
    return this.configuration.workbenchConfigurationPlans.some(
      p =>
        p.materialThickness.canHaveJambs &&
        p.shape.shapeType !== ShapeTypeEnum.Angle &&
        p.shape.shapeType !== ShapeTypeEnum.Circle
    );
  }

  configurationCanHaveCutaway(): boolean {
    // On veut qu'il y ai au moins un plan différent du Plan rond et que tout les plans soit différent du Quartz car pas de découpe d'angle en quartz.
    return (
      this.configuration.workbenchConfigurationPlans.some(p => p.shape.shapeType !== ShapeTypeEnum.Circle) &&
      this.configuration.workbenchConfigurationPlans.every(p => p.material.materialType != MaterialTypeEnum.Quartz)
    );
  }

  configurationCanHaveCutting(): boolean {
    // Si tout les plans sont en quartz je peux avoir des découpe.
    return this.configuration.workbenchConfigurationPlans.every(
      p => p.material.materialType === MaterialTypeEnum.Quartz
    );
  }

  getPlansWithJambs(): ConfigurationPlan[] {
    return this.configuration.workbenchConfigurationPlans.filter(p => !p.isInGroup && p.configurationJambs?.length > 0);
  }

  getGroupsWithJambs(): ConfigurationGroup[] {
    return this.configuration.workbenchConfigurationGroups.filter(g =>
      g.workbenchConfigurationPlans.some(p => p.configurationJambs?.length > 0)
    );
  }

  selectGroupOrPlan(planOrGroup: ConfigurationPlan | ConfigurationGroup): void {
    this.planOrGroupSelected = planOrGroup;
  }

  planOrGroupIsVisible(planOrGroup: ConfigurationPlan | ConfigurationGroup): boolean {
    // vérifie si le plan ou groupe sélectionné est toujours existant
    if (
      this.planOrGroupSelected != null &&
      !this.configuration.workbenchConfigurationPlans
        .filter(p => p.configurationJambs.length > 0)
        .some(p => p.id === this.planOrGroupSelected.id)
    ) {
      this.planOrGroupSelected = null;
    }

    if (
      this.planOrGroupSelected === null &&
      this.configuration.workbenchConfigurationPlans.some(p => p.configurationJambs.length > 0)
    ) {
      const plan = this.configuration.workbenchConfigurationPlans.find(p => p.configurationJambs.length > 0);

      if (plan.isInGroup) {
        this.planOrGroupSelected = this.configuration.workbenchConfigurationGroups.find(g =>
          g.workbenchConfigurationPlans.some(p => p.id === plan.id)
        );
      } else {
        this.planOrGroupSelected = plan;
      }
    }

    return planOrGroup === this.planOrGroupSelected;
  }
}
